import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef } from "react";
import { useStore } from "../../helpers";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { safe_get } from "../../report-visuals";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useQuery } from "@tanstack/react-query";
import { default_web_features } from "./web-featues";
import ClientProfile from "../../components/client-profile";
import { RenderFeatureCard } from "./render-feature-card";
import { useShowClientProfile } from "./useShowClientProfile";
import { canAccessQFast } from "../../features/qfast-dashboard/constants";

const ProjectDashboardPage = observer((props) => {
    const rootStore = useStore();
    const navigate = useNavigate();
    const { projectid } = useParams();

    const { userStore, projectStore, surveyResponseStore, digitalArchiveStore, flexiStore } = rootStore;

    const role = userStore.user["role"];
    const [t] = useTranslation("ProjectDashboard");
    const track_voter_record_config = useRef({});
    const show_voter_reg_card = useRef(false);
    const view_task_permission = useRef(false);
    // Note: Project data is already loaded by AuthenticatedLayout
    // No need to call useGetFullProject() here - it would cause duplicate API calls
    const { showClientProfile } = useShowClientProfile();

    useEffect(() => {
        // Whenever active project changes in MobX, check if URL is synced
        const currentId = projectStore.activeProject?.id;

        if (currentId && currentId.toString() !== projectid) {
            navigate(`/project/${currentId}`, { replace: true });
        }
    }, [projectStore.activeProject?.id, projectid, navigate]);

    // Permissions for different project features.
    const view_beneficiary_permission = projectStore.getPermission("manage_fs_beneficiary", false);
    const view_grievance_permission = projectStore.getPermission("manage_fs_grievance", false);
    const view_projectworks_permission = projectStore.getPermission("manage_fs_projectworks", false);

    const { isLoading: projectsLoading } = useQuery({
        queryKey: ["projects-list"],
        queryFn: async () => await userStore.getProjects(),
        onSuccess: (data) => {
            if (!projectid || projectid === "undefined") {
                const default_project_id = safe_get(data[0], "id", "1");
                navigate(`project/${default_project_id}`, { replace: true });
            }
        },
        enabled: projectid === "undefined" || projectid === undefined,
    });

    // Get project-specific web features or fallback to default features.
    const webFeatures = projectStore.getProjectProperty("web_features", default_web_features);

    const isRootOrClient = userStore.isRoot() || userStore.isClient();

    useEffect(() => {
        // Fetch configuration for tracking voter records and set related states.
        // Note: Project data is already loaded by AuthenticatedLayout
        track_voter_record_config.current = projectStore.getProjectProperty("track_voter_reg_eci", {});
        show_voter_reg_card.current = safe_get(track_voter_record_config, "enabled", false);
        view_task_permission.current = projectStore.getPermission("view_tasks", false);

        // Reset filters and digital archive store properties.
        surveyResponseStore.reset_new_filters();
        digitalArchiveStore.update_DAStoreProp("activeParentId", "");

        // Reset page number in flexiStore whenever the project changes.
        flexiStore.reset_fsd_search();
    }, [projectid]);

    // useMemo hook to memoize the menu list generation based on project features.
    const populateMenuList = useMemo(() => {
        const pm = { id: 1, heading: t("project_management"), subItems: [] };
        const fw = { id: 2, heading: t("field_work"), subItems: [] };
        const cm = { id: 3, heading: t("management"), subItems: [] };

        // Loop through each feature in webFeatures and populate the menu sections based on the feature status.
        for (let key of Object.keys(webFeatures)) {
            const val = webFeatures[key];
            // console.log("Project keys");
            // console.log(key);
            if (val === "enabled") {
                switch (key) {
                    // case "Taskmanager V2":
                    case "beneficiary":
                        if (view_beneficiary_permission || isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-hands-helping",
                                name: t("beneficiary"),
                                path: "/project/" + projectid + "/beneficiary_management",
                            });
                        }
                        break;
                    case "grievance":
                        if (view_grievance_permission || isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: t("grievance"),
                                path: "/project/" + projectid + "/grievance_management",
                            });
                        }
                        break;

                    case "service_requests":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: t("service_requests"),
                                path: "/project/" + projectid + "/servicerequests",
                            });
                        }
                        break;

                    case "task_manager_v2":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Task Manager V2",
                                path: "/project/" + projectid + "/taskmanager_v2",
                            });
                        }
                        break;
                    case "dvg_cmp":
                        if (isRootOrClient && projectid === "940") {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Davangere PC CMP",
                                path: "/pbidashboard/davanagere_cmp",
                            });
                        }
                        break;
                    case "profiling":
                        if (isRootOrClient && projectid === "947") {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Karur Profiling",
                                path: "/pbidashboard/karur_profiling",
                            });
                        }
                        break;
                    case "hoskote_dashboard":
                        if (isRootOrClient && projectid === "630") {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Hoskote CMP",
                                path: "/pbidashboard/hoskote_cmp",
                            });
                        }
                        break;
                    case "muddebihal_analytics":
                        if (isRootOrClient && projectid === "979") {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Muddebihal Analytics",
                                path: "/pbidashboard/muddebihal_analytics",
                            });
                        }
                        break;
                    case "grievance_management_v2":
                        if (view_grievance_permission || isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-face-sad-tear",
                                name: "Grievance management V2",
                                path: "/project/" + projectid + "/grievance_management_v2",
                            });
                        }
                        break;
                    case "project_works":
                        if (isRootOrClient || view_projectworks_permission) {
                            cm.subItems.push({
                                icon: "fa-solid fa-diagram-project",
                                name: t("project"),
                                path: "/project/" + projectid + "/project_works",
                            });
                        }
                        break;
                    case "c3":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-grid-horizontal",
                                name: t("command"),
                                path: "/project/" + projectid + "/c3",
                            });
                        }
                        break;
                    case "analytical_dashboard":
                        if (isRootOrClient && projectid == "621") {
                            cm.subItems.push({
                                icon: "fa-solid fa-chart-simple",
                                name: "Analytial Dashboard",
                                path: "/dashboard/kopargaon",
                            });
                        }
                        break;
                    case "manage_voters":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-poll-people",
                                name: t("manage"),
                                path: "/project/" + projectid + "/manage-voters",
                            });
                        }
                        break;
                    case "voter_reach_out":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-comments",
                                name: t("reach"),
                                path: "/project/" + projectid + "/voter_reach_out",
                            });
                        }
                        break;
                    case "election_day_dashboard":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-table",
                                name: t("election"),
                                path: "/project/" + projectid + "/election_day_dashboard",
                            });
                        }
                        break;
                    case "voter_registration_tracker":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-person-booth",
                                name: t("tracker"),
                                path: "/project/" + projectid + "/voter_registration_tracker",
                            });
                        }
                        break;
                    case "digital_repository":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-person-booth",
                                name: t("Digital Repository"),
                                path: "/project/" + projectid + "/digital_repository",
                            });
                        }
                        break;
                    case "letters_repository":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fa-solid fa-envelope",
                                name: t("Letters Repository"),
                                path: "/project/" + projectid + "/letters_repository",
                            });
                        }
                        break;
                    case "booth_selection":
                        if (isRootOrClient) {
                            fw.subItems.push({
                                icon: "fa-solid fa-person-booth",
                                name: "Booth Selection",
                                path: "/project/" + projectid + "/booth_selection",
                            });
                        }
                        break;
                    case "field_payments":
                        const WHITELIST_FOR_USERS = [9669, 9174, 74, 1, 92, 10693, 292, 11717, 11780];
                        const has_permission = WHITELIST_FOR_USERS.includes(userStore.user.id);
                        if (has_permission) {
                            fw.subItems.push({
                                icon: "fas fa-wallet",
                                name: t("field_pay"),
                                path: "/project/" + projectid + "/fieldpayment",
                            });
                        }
                        break;
                    case "field_team_report":
                        fw.subItems.push({
                            icon: "fas fa-file-chart-line",
                            name: t("team_report"),
                            path: "/project/" + projectid + "/fieldteamreport",
                        });
                        break;
                    case "field_team_report_v2":
                        fw.subItems.push({
                            icon: "fas fa-file-chart-line",
                            name: "Field Team Report V2",
                            path: "/project/" + projectid + "/selectsurvey",
                        });
                        break;
                    case "taskmanager":
                        // Adding Taskmanager card if the user has permission to view tasks or is root/client.
                        if (view_task_permission.current || role === "root" || role === "client") {
                            fw.subItems.push({
                                icon: "fa-solid fa-list-check",
                                name: t("task"),
                                path: "/project/" + projectid + "/taskmanager",
                            });
                        }
                        break;
                    case "manage_project":
                        // Only root users can access project management.
                        if (
                            userStore.isRoot() ||
                            userStore.user.id === 16003 ||
                            userStore.user.id === 22274
                        ) {
                            pm.subItems.push({
                                icon: "fa-solid fa-cogs",
                                name: t("manage_project"),
                                path: `/project/${projectid}/manage`,
                            });
                        }
                        break;
                    case "user_management":
                        if (userStore.isRoot() || userStore.isClient()) {
                            pm.subItems.push({
                                icon: "fa-solid fa-users",
                                name: t("user_manage"),
                                path: "/project/" + projectid + "/usermanagement",
                            });
                        }
                        break;
                    case "beneficiary_v2":
                        if (userStore.isRoot() || userStore.isClient()) {
                            cm.subItems.push({
                                icon: "fa-solid fa-users",
                                name: "Beneficiary V2",
                                path: "/project/" + projectid + "/beneficiary_v2",
                            });
                        }
                        break;
                    case "project_works_v2":
                        if (userStore.isRoot() || userStore.isClient()) {
                            cm.subItems.push({
                                icon: "fa-solid fa-users",
                                name: "PW V2",
                                path: "/project/" + projectid + "/projectworks_v2",
                            });
                        }
                        break;
                    case "influential_leaders":
                        if (userStore.isRoot() || userStore.isClient()) {
                            cm.subItems.push({
                                icon: "fas fa-user-friends",
                                name: "Influential Leaders",
                                path: "/project/" + projectid + "/influential_leaders",
                            });
                        }
                        break;
                    case "events_calendar":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Events Calendar",
                                path: "/project/" + projectid + "/calendar",
                            });
                        }
                        break;
                    case "watch_man":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Watch Man",
                                path: "/project/" + projectid + "/watchman",
                            });
                        }
                        break;
                    case "my_schemes_demo":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "My Schemes",
                                path: "/project/" + projectid + "/scheme",
                            });
                        }
                        break;

                    case "resume_builder":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Resume Builder",
                                path: "/project/" + projectid + "/resume-builder/landing",
                            });
                        }
                        break;

                    case "family_grouping":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Family Grouping",
                                path: "/project/" + projectid + "/family-grouping/family",
                            });
                        }
                        break;

                    case "notes":
                        if (isRootOrClient) {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Notes",
                                path: "/project/" + projectid + "/notes",
                            });
                        }
                        break;
                    case "qfast_dashboard":
                        if (canAccessQFast(userStore)) {
                            cm.subItems.push({
                                icon: "fa-solid fa-chart-line",
                                name: "QFast Dashboard",
                                path: "/project/" + projectid + "/qfast",
                            });
                        }
                        break;
                    case "hoskote_agri":
                        if (isRootOrClient && projectid === "1098") {
                            cm.subItems.push({
                                icon: "fas fa-list-ul",
                                name: "Report",
                                path: "/pbidashboard/hosakote_agriculture",
                            });
                        }
                        break;
                }
            }
        }
        return [cm, fw, pm];
    }, [webFeatures, projectid, userStore]);

    return (
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-2 md:gap-2 lg:gap-3 xl:gap-4">
            <div className={`${showClientProfile ? "w-full sm:w-[75%]" : "w-full"} min-w-0`}>
                <div className="flex flex-col gap-y-1 pl-2 sm:gap-y-2 sm:pl-4 md:gap-y-3 lg:gap-y-4 xl:gap-y-5">
                    {_.sortBy(populateMenuList, (item) => item.id).map((feature, index) => (
                        <RenderFeatureCard key={index} feature={feature} />
                    ))}
                </div>
            </div>

            <Outlet />
        </div>
    );
});

export default ProjectDashboardPage;
